import type { GuessRow } from "../api";
import type { KeyState } from "../game/keyboard";
import { Tile } from "./Tile";

interface GridProps {
  guesses: GuessRow[];
  current: string;
  /** Row whose reveal animation is playing (the just-submitted guess). */
  revealRow: number | null;
  shakeRow: number | null;
  bounceRow: number | null;
  finished: boolean;
}

const ROWS = 6;
const COLS = 5;

export function Grid({ guesses, current, revealRow, shakeRow, bounceRow, finished }: GridProps) {
  return (
    <div
      className="mx-auto grid w-full grid-rows-6 gap-[6px] px-2"
      // Width-bound on wide screens, height-bound on short ones: 6 rows + gaps must fit
      // between the header (~52px) and the submit button + keyboard (~280px).
      style={{
        maxWidth:
          "min(350px, calc((var(--tg-viewport-stable-height, 100vh) - 350px) * 5 / 6))",
      }}
    >
      {Array.from({ length: ROWS }, (_, r) => {
        const g = guesses[r];
        const isActive = r === guesses.length && !finished;
        const letters = g
          ? g.word.split("")
          : isActive
            ? current.padEnd(COLS, " ").split("").map((c) => c.trim())
            : Array.from({ length: COLS }, () => "");
        const states: (KeyState | null)[] = g
          ? (g.marks.split("") as KeyState[])
          : Array.from({ length: COLS }, () => null);

        return (
          <div key={r} className={`grid grid-cols-5 gap-[6px] ${shakeRow === r ? "row-shake" : ""}`}>
            {letters.map((letter, c) => (
              <Tile
                // Remount the tile when a letter lands so the pop replays.
                key={isActive ? `${c}:${letter}` : c}
                letter={letter}
                state={states[c] ?? null}
                reveal={revealRow === r}
                bounce={bounceRow === r && revealRow !== r}
                index={c}
                pop={isActive && letter !== "" && c === current.length - 1}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
